import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useQueryClient } from 'react-query';

import useConfiguration from 'app/hooks/useConfiguration';
import useDestinations from 'app/hooks/useDestinations';
import useGuestDataQueryByLocator from 'app/hooks/useGuestDataQueryByLocator';
import useStore from 'app/hooks/useStore';
import { getEntitlements } from 'app/services/GuestCenterHelpers';
import {
  Entitlement,
  EntitlementReservation,
  GuestData,
  GuestDataResponse,
  TravelParty,
} from 'app/services/GuestCenterService.types';
import { QueryKeys } from 'app/services/QueryClient';

export type TravelPartyContextProps = {
  travelParty: TravelParty | null;
  passes: GuestDataResponse['guestData']['passes'] | null;
  reservations: EntitlementReservation[] | null;
  entitlements: Entitlement[] | null;
  activeEntitlement: Entitlement | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  hasActiveTravelParty: boolean;
  mergeReservationResponse: (guestData: GuestData) => void;

  // New helpers for ProductLineup integration
  getEntitlementForProduct: (productContentKey: string) => Entitlement | undefined;
  hasEntitlementForProduct: (productContentKey: string) => boolean;
};

export const TravelPartyContext = createContext<TravelPartyContextProps>({
  travelParty: null,
  passes: null,
  reservations: null,
  entitlements: [],
  activeEntitlement: null,
  isLoading: false,
  isFetching: false,
  error: null,
  refetch: () => {},
  hasActiveTravelParty: false,
  mergeReservationResponse: () => {},
  getEntitlementForProduct: () => undefined,
  hasEntitlementForProduct: () => false,
});

export default function TravelPartyProvider({ children }: { children: ReactNode }) {
  const { data: configurationResponse } = useConfiguration({ notifyOnChangeProps: ['data'] });
  const { data: destinationsContent } = useDestinations({ notifyOnChangeProps: ['data'] });

  const activeEntitlementKey = useStore((s) => s.entitlementKey);
  const email = useStore((s) => s.email);
  const locator = activeEntitlementKey || '';
  const queryClient = useQueryClient();

  const productLineups = useMemo(
    () => configurationResponse?.productLineups.configuration ?? [],
    [configurationResponse]
  );
  const attractionConfigs = useMemo(
    () => configurationResponse?.attractions.configuration ?? [],
    [configurationResponse]
  );

  const {
    data: byLocatorData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGuestDataQueryByLocator({
    locator,
    enabled: !!locator,
    notifyOnChangeProps: ['data', 'status'],
  });

  const entitlements = useMemo(() => {
    if (!byLocatorData) return [];
    return getEntitlements({
      guestDataResponse: byLocatorData,
      productLineups,
      attractionConfigs,
      destinationsContent: destinationsContent ?? [],
    });
  }, [byLocatorData, productLineups, attractionConfigs, destinationsContent]);

  const activeEntitlement = useMemo(() => {
    if (!activeEntitlementKey) return null;
    return entitlements.find((e) => e.isValid && e.entitlementKey === activeEntitlementKey) || null;
  }, [entitlements, activeEntitlementKey]);

  const travelParty = useMemo(() => {
    return activeEntitlement?.travelParty ?? null;
  }, [activeEntitlement]);

  const passes = useMemo(() => {
    return activeEntitlement?.passes ?? null;
  }, [activeEntitlement]);

  const reservations = useMemo(() => {
    return activeEntitlement?.reservations ?? null;
  }, [activeEntitlement]);

  const mergeReservationResponse = useCallback(
    (guestData: GuestData) => {
      // Update the locator-specific query with the new guest data
      const existingLocatorData = queryClient.getQueryData<GuestDataResponse>([
        QueryKeys.GuestDataByLocator,
        email,
        locator,
      ]);

      if (existingLocatorData) {
        queryClient.setQueryData([QueryKeys.GuestDataByLocator, email, locator], {
          ...existingLocatorData,
          guestData,
        });
      }

      // Update the main guest data query
      const existingData = queryClient.getQueryData<GuestDataResponse>([QueryKeys.GuestData]);

      if (existingData) {
        const existingGuestDataResponse = existingData as GuestDataResponse;
        queryClient.setQueryData([QueryKeys.GuestData], {
          ...existingGuestDataResponse,
          guestData: {
            ...existingGuestDataResponse.guestData,
            travelParties: existingGuestDataResponse.guestData.travelParties.map((tp) =>
              tp.locator === locator
                ? guestData.travelParties.find((rtp) => rtp.locator === locator) || tp
                : tp
            ),
            passes: [
              ...existingGuestDataResponse.guestData.passes.filter((p) => p.locator !== locator),
              ...guestData.passes,
            ],
            reservations: [
              ...existingGuestDataResponse.guestData.reservations.filter((r) => {
                const reservationPassBarcodes = new Set(
                  r.tickets?.map((ticket) => ticket.passBarcode).filter(Boolean) ?? []
                );
                return !Array.from(reservationPassBarcodes).some((b) =>
                  guestData.passes.some((p) => p.barcode === b)
                );
              }),
              ...guestData.reservations,
            ],
          },
        });
      }
    },
    [email, locator, queryClient]
  );

  // New helper functions for ProductLineup integration
  const getEntitlementForProduct = useCallback(
    (productContentKey: string) => {
      return entitlements.find((e) => e.isValid && e.productContentKey === productContentKey);
    },
    [entitlements]
  );

  const hasEntitlementForProduct = useCallback(
    (productContentKey: string) => {
      return entitlements.some((e) => e.isValid && e.productContentKey === productContentKey);
    },
    [entitlements]
  );

  const value = useMemo(
    () => ({
      travelParty,
      passes,
      reservations,
      entitlements,
      activeEntitlement,
      isLoading: isLoading || false,
      isFetching: isFetching || false,
      error: error as Error | null,
      refetch,
      hasActiveTravelParty: !!locator,
      mergeReservationResponse,
      getEntitlementForProduct,
      hasEntitlementForProduct,
    }),
    [
      travelParty,
      passes,
      reservations,
      entitlements,
      activeEntitlement,
      isLoading,
      isFetching,
      error,
      refetch,
      locator,
      mergeReservationResponse,
      getEntitlementForProduct,
      hasEntitlementForProduct,
    ]
  );

  return <TravelPartyContext.Provider value={value}>{children}</TravelPartyContext.Provider>;
}

export const useTravelPartyContext = () => useContext(TravelPartyContext);
